import React from 'react'

import { withStyles } from '@material-ui/core/styles'
import includes from 'lodash/includes'
import toLower from 'lodash/toLower'
import trim from 'lodash/trim'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'

import * as Model from 'api/model'
import * as PoziUtils from 'api/utils'
import AccordionWrapper from 'drawer/tabs/AccordionWrapper'
import { SET_LAYER_LOAD_FAILED, setGroupCollapsed, setGroupExpanded, setLayerSelected, setLayerUnselected } from 'drawer/tabs/layers/actions'
import LayerPanels from 'drawer/tabs/layers/panels/LayerPanels'
import { layerValidInCurrentProfile } from 'utils/profile'

const styles = theme => ({
  root: {},
  layersInGroup: {},
  filtered: {
    display: 'none'
  },
  notFiltered: {},
  wrapper: {
    // padding: 1
  },
  title: {
    textTransform: 'none',
    // lineHeight: 1.4,
    fontSize: 15,
    paddingTop: 2,
    paddingLeft: 9,
    color: '#303030',
    letterSpacing: 'inherit'
  }
})

class Group extends React.Component {
  state = {
    keywords: ''
  }

  componentDidMount () {
    const keywords = this.getKeywordsFromLayersInGroup()

    this.setState({ keywords })

    // Expand any group that has a layer matching the layer search filter
    this.expandGroupIfFilterMatches()

    // Process basemap group to make sure
    this.selectFirstBasemapLayerIfNoneSelected()
  }

  componentDidUpdate = (prevProps) => {
    const { currentProfile, dispatchSetGroupCollapsed, group, tabLayersFilter } = this.props

    // Expand any group that has a layer matching the layer search filter
    this.expandGroupIfFilterMatches()

    // After clearing layer search, only expand groups that have a selected layer in them
    const prevTabLayersFilter = prevProps.tabLayersFilter
    const nextTabLayersFilter = tabLayersFilter

    if (prevTabLayersFilter && !nextTabLayersFilter) {
      const shouldCollapse = group.expanded && !this.groupHasSelectedLayers()
      if (shouldCollapse) {
        dispatchSetGroupCollapsed(group)
      }
    }

    // Process basemap group upon profile change
    const profileChanged = prevProps.currentProfile !== this.props.currentProfile
    if (profileChanged && group.id === 'basemap') {
      this.selectFirstBasemapLayerIfNoneSelected()
    }

    // Make sure that we always show a basemap layer
    this.selectNextBasemapLayerIfLoadFailed()
  }

  expandGroupIfFilterMatches = () => {
    const { group, dispatchSetGroupExpanded, tabLayersFilter } = this.props

    // console.debug('Group - expandGroupIfFilterMatches', group.id, tabLayersFilter)

    const { keywords } = this.state

    const tabLayersFilterLC = toLower(trim(tabLayersFilter))
    const bIncludesFilter = includes(keywords, tabLayersFilterLC)

    if (tabLayersFilterLC && bIncludesFilter && !group.expanded) {
      dispatchSetGroupExpanded(group)
    }
  }

  /**
 * Get all keywords from all layers in group
 */
  getKeywordsFromLayersInGroup = () => {
    const { group, layersInGroup } = this.props

    // console.debug('populateLayersInGroup', group.id, Object.keys(layerConfs))

    const keywordsArray = []

    // Add group name to filter
    keywordsArray.push((group.name || ''))

    for (const layerConf of layersInGroup) {
      const { titleid } = layerConf

      // console.debug('getKeywordsFromLayersInGroup - layer', titleid, layerConf)

      // Get keywords property from layers in Model.get('config.layers') because
      // it is currently not stored in redux
      const keywordsFromLayer = PoziUtils.getLayerFromTitleID(titleid)?.keywords

      if (!keywordsFromLayer || !keywordsFromLayer.length) {
        continue
      }

      keywordsArray.push(keywordsFromLayer)
    }

    // Create keywords string and remove any double spaces
    const keywords = keywordsArray.join(' ').toLowerCase().replace(/  +/g, ' ').trim()

    // console.debug('getKeywordsFromLayersInGroup', group, keywords)

    return keywords
  }

  selectFirstBasemapLayerIfNoneSelected = () => {
    const { groupid, layersInGroup, dispatchSetLayerSelected } = this.props

    // Only process basemap group
    if (groupid !== 'basemap') {
      return
    }

    // console.debug('Group - selectFirstBasemapLayerIfNoneSelected', layersInGroup)

    // Loop through all layers in group and determine the first layer and
    // if there are any visible layers
    let firstLayerInGroup
    let groupHasVisibleLayers = false
    const layerConfs = layersInGroup
    for (const layerConf of layerConfs) {
      // Ignore layers of capabilities type
      if (PoziUtils.isLayerTypeCatalogue(layerConf)) {
        continue
      }

      const { loadstatus, titleid } = layerConf

      if (loadstatus === SET_LAYER_LOAD_FAILED) {
        console.debug('Group - selectFirstBasemapLayerIfNoneSelected - layer failed to load. Ignoring.', titleid)

        continue
      }

      // Check if the layer is valid in the current profile.
      const layerIsValidInCurrentProfile = layerValidInCurrentProfile(titleid)
      if (!layerIsValidInCurrentProfile) {
        continue
      }

      // Capture first layer in group
      if (!firstLayerInGroup) {
        firstLayerInGroup = layerConf
      }

      // Exit the loop upon first encounter if a visible layer
      if (layerConf.selected || layerConf.visible) {
        groupHasVisibleLayers = true
        break
      }
    }

    // If there is at least one layer and no layers in the group were visible,
    // make the first layer visible
    if (firstLayerInGroup && !groupHasVisibleLayers) {
      dispatchSetLayerSelected(firstLayerInGroup.titleid)
    }
  }

  selectNextBasemapLayerIfLoadFailed = () => {
    const { groupid, layersInGroup, dispatchSetLayerSelected } = this.props

    // Only process basemap group
    if (groupid !== 'basemap') {
      return
    }

    // console.debug('Group - selectNextBasemapLayerIfLoadFailed', groupid, layersInGroup)

    let shouldSelectNextLayer = false

    const layerConfs = Object.values(layersInGroup)
    for (const layerConf of layerConfs) {
      // console.debug('Group - selectNextBasemapLayerIfLoadFailed - layer', layerConf.titleid, layerConf)

      const { loadstatus, selected, titleid } = layerConf
      if (selected && loadstatus === SET_LAYER_LOAD_FAILED) {
        // console.debug('Group - selectNextBasemapLayerIfLoadFailed - layer failed to load. Attempting to select next layer.', titleid)

        shouldSelectNextLayer = true
        continue
      }

      // Exclude catalogue layers from being selected
      const layerIsCatalogue = PoziUtils.isLayerTypeCatalogue(layerConf)
      if (layerIsCatalogue) {
        // console.debug('Group - selectNextBasemapLayerIfLoadFailed - layer is catalogue. Skipping.', titleid)
        continue
      }

      if (!selected && loadstatus !== SET_LAYER_LOAD_FAILED && shouldSelectNextLayer) {
        // console.debug('Group - selectNextBasemapLayerIfLoadFailed - selecting next layer:', titleid)

        dispatchSetLayerSelected(titleid)
        break
      }
    }

    if (shouldSelectNextLayer) {
      // console.debug('Group - selectNextBasemapLayerIfLoadFailed - no more basemap layers to select')
    }
  }

  onChange = () => {
    // console.debug('Group - onChange')

    this.toggleExpansionPanel()
  }

  toggleExpansionPanel () {
    const { group, dispatchSetGroupExpanded, dispatchSetGroupCollapsed } = this.props

    const shouldExpand = !group.expanded

    console.debug('Group - toggleExpansionPanel', group.id, shouldExpand)

    if (shouldExpand) {
      dispatchSetGroupExpanded(group)
    } else {
      dispatchSetGroupCollapsed(group)
    }
  }

  renderLayersForGroup () {
    const { groupid, layersInGroup } = this.props
    const layersMarkup = []
    for (const layerConf of layersInGroup) {
      const { titleid } = layerConf

      layersMarkup.push(<LayerPanels key={titleid} groupid={groupid} titleid={titleid} />)
    }

    // console.debug('renderLayersForGroup - group', groupid, Object.keys(layersInGroup), layersMarkup)

    return layersMarkup
  }

  groupHasLayersToShow () {
    const { layersInGroup } = this.props

    return layersInGroup.some(layerConf => layerConf.showInLayerControl)
  }

  groupHasLayersWithCurrentProfile () {
    const { layersInGroup } = this.props

    return layersInGroup.some(({ titleid }) => layerValidInCurrentProfile(titleid))
  }

  groupHasLocalDataSource () {
    const { layersInGroup } = this.props
    for (const layerConf of layersInGroup) {
      if (layerConf.localDataSource) {
        return true
      }
    }
    return false
  }

  groupHasSelectedLayers () {
    const { layersInGroup } = this.props
    for (const layerConf of layersInGroup) {
      // We treat capabilities layers differently from other layers: determine
      // if any of the child layers is visible (therefore selected)
      const layerIsCatalogue = PoziUtils.isLayerTypeCatalogue(layerConf)
      if (layerIsCatalogue) {
        const titleid = layerConf.titleid
        const olLayer = PoziUtils.getOpenLayersObjectFromTitleID(titleid)
        if (!olLayer) {
          console.log('groupHasSelectedLayers - catalogue layer - cannot find OL olayer', titleid)

          continue
        }

        const hasSelectedChildLayers = olLayer.getLayersArray().some(l => l.getVisible())
        if (hasSelectedChildLayers) {
          return true
        }
      }

      if (!layerIsCatalogue && layerConf.selected) {
        // console.debug('groupHasSelectedLayers - selected & visible', layerConf.titleid, layerConf.type)
        return true
      }
    }
    return false
  }

  groupHasSelectedLayersWithLegend () {
    const { groupid, layersInGroup } = this.props

    // console.debug('groupHasSelectedLayersWithLegend - layers in group', groupid, layersInGroup)

    for (const layerConf of layersInGroup) {
      // We treat capabilities layers differently from other layers: determine
      // if any of the child layers is visible (therefore selected)
      const layerIsCatalogue = PoziUtils.isLayerTypeCatalogue(layerConf)
      if (layerIsCatalogue) {
        const titleid = layerConf.titleid
        const olLayer = PoziUtils.getOpenLayersObjectFromTitleID(titleid)
        if (!olLayer) {
          console.log('groupHasSelectedLayersWithLegend - catalogue layer - cannot find OL olayer', titleid)

          continue
        }

        const childLayerConfs = olLayer.getLayersArray().map(olLayer => PoziUtils.getLayerFromTitleID(olLayer.get('titleid')))

        const hasSelectedChildLayersWithLegend = childLayerConfs.some(PoziUtils.layerShowsLegend)
        if (hasSelectedChildLayersWithLegend) {
          return true
        }
      } else {
        const layerShowsLegend = PoziUtils.layerShowsLegend(layerConf)

        // console.debug('groupHasSelectedLayersWithLegend - layerShowsLegend', groupid, layerConf.titleid, layerShowsLegend)

        if (layerShowsLegend) {
          return true
        }
      }
    }

    return false
  }

  render () {
    const { classes, groupid, group, layersInGroup, printUIVisible, tabLayersFilter } = this.props

    const { keywords } = this.state

    // console.debug('Group render', groupid, layersInGroup, printUIVisible, tabLayersFilter, group)

    // Don't render groups without a groupid
    if (!groupid) {
      // console.debug('Group has no id', groupid, group)

      return null
    }

    // Don't render groups without any layers
    const groupHasLayers = this.groupHasLayersToShow()
    if (!groupHasLayers) {
      // console.log('Group - render: group has no (valid) layers', groupid, layersInGroup, groupHasLayers)

      return null
    }

    // console.debug('Render layer group', groupid)

    // Check if there is a current profile and if all the layers in the group either have no profile
    // match that current profile.
    // This is to prevent a potentially empty group showing.
    const groupHasLayersWithCurrentProfile = this.groupHasLayersWithCurrentProfile()
    if (!groupHasLayersWithCurrentProfile) {
      // console.debug('Group - render: group has no (valid) layers with current profile', groupid, layersInGroup, groupHasLayersWithCurrentProfile)

      return null
    }

    // Hide layer groups using the 'layergroup' URL hash param directive. If 'layergroup' is set and this group is not part of the list, then hide.
    const stateArrayLayerGroups = Model.getStateArray('layergroup', [])

    const groupNotInLayerGroupHash = stateArrayLayerGroups.length > 0 && !stateArrayLayerGroups.includes(groupid)
    if (groupNotInLayerGroupHash) {
      console.debug('Group - render: layergroup is set in URL hash params and this group is not part of the list. Hiding.', groupid, stateArrayLayerGroups)

      return null
    }

    // Don't show groups that don't match the layer search keywords
    // We do not apply this filter if we are in 'print mode'
    let filteredClass = classes.notFiltered

    // When in print mode, only show groups with layers that have a legend
    if (printUIVisible) {
      const groupHasVisibleLayersWithLegend = this.groupHasSelectedLayersWithLegend()

      // console.debug('groupHasVisibleLayersWithLegend', groupid, groupHasVisibleLayersWithLegend)

      if (!groupHasVisibleLayersWithLegend) {
        filteredClass = classes.filtered
      }
    }

    // Check for layer filter match but not when we are in print mode
    const tabLayersFilterLC = toLower(trim(tabLayersFilter))
    const bIncludesFilter = includes(keywords, tabLayersFilterLC)
    if (!printUIVisible && tabLayersFilterLC !== '' && !bIncludesFilter) {
      // CSS masking for performance. https://stackoverflow.com/a/24590605/1550587
      filteredClass = classes.filtered
      // return null

      // console.debug('Filtered group', groupid, tabLayersFilterLC, bIncludesFilter)
    }

    const details = (
      <div className={classes.layersInGroup}>
        {this.renderLayersForGroup()}
      </div>
    )

    const showLockIcon = this.groupHasLocalDataSource() && !printUIVisible
    const groupHasSelectedLayers = this.groupHasSelectedLayers()
    const isTitleBold = (!group.expanded && groupHasSelectedLayers) || printUIVisible

    // When layer search is active, disable accordion functionality
    // (we don't want the user to collapse groups when search is active)
    const disableAccordion = !!tabLayersFilter
    const bExcludeExpandIcon = disableAccordion

    const onChangeStopEventPropagation = (evt) => {
      evt.preventDefault()
      evt.stopPropagation()
    }

    const onChange = disableAccordion ? onChangeStopEventPropagation : this.onChange

    // Force expanded setting if print mode is active
    const expanded = group.expanded || printUIVisible

    return (
      <div className={filteredClass}>
        <div className={classes.root} id={groupid}>
          <AccordionWrapper
            bExcludeExpandIcon={bExcludeExpandIcon}
            bLockIcon={showLockIcon}
            rootClass={classes.wrapper}
            titleClass={classes.title}
            expanded={expanded}
            onChange={onChange}
            title={group.name}
            isTitleBold={isTitleBold}
            treeView={true}
          >
            {details}
          </AccordionWrapper>
        </div>
      </div>
    )
  }
}

Group.propTypes = {
  classes: PropTypes.object.isRequired,
  currentProfile: PropTypes.string,
  groupid: PropTypes.string.isRequired,
  group: PropTypes.object.isRequired,
  layersInGroup: PropTypes.array.isRequired,
  printUIVisible: PropTypes.bool,
  profiles: PropTypes.object,
  tabLayersFilter: PropTypes.string.isRequired,
  dispatchSetGroupCollapsed: PropTypes.func.isRequired,
  dispatchSetGroupExpanded: PropTypes.func.isRequired,
  dispatchSetLayerSelected: PropTypes.func.isRequired,
  dispatchSetLayerUnselected: PropTypes.func.isRequired
}

function mapStateToProps (state, ownProps) {
  const { currentProfile, groups, layers, printUIVisible, profiles, tabLayersFilter } = state

  const group = groups[ownProps.groupid]

  const layersInGroup = group.layers.map(titleid => layers[titleid])

  const props = {
    currentProfile,
    group,
    layersInGroup,
    printUIVisible,
    profiles,
    tabLayersFilter
  }

  return props
}

const mapDispatchToProps = dispatch => {
  return {
    dispatchSetGroupExpanded: group => {
      dispatch(setGroupExpanded(group))
    },
    dispatchSetGroupCollapsed: group => {
      dispatch(setGroupCollapsed(group))
    },
    dispatchSetLayerSelected: layer => {
      dispatch(setLayerSelected(layer))
    },
    dispatchSetLayerUnselected: layer => {
      dispatch(setLayerUnselected(layer))
    }
  }
}

export default (
  withStyles(styles)(
    connect(
      mapStateToProps,
      mapDispatchToProps
    )(Group)
  )
)
